import type { DisplayState, OrderPanel, StateConfig, StateDefinition } from './state.types'

/**
 * Interface for status options derived from state configuration
 */
export interface StatusOption {
  value: string
  label: string
  color?: string
  progressionLevel?: number
}

/**
 * Selects a dictionary of state definitions from the state configuration.
 *
 */
export function selectStateDictionary(states: StateConfig['states'] | undefined): Record<string, StateDefinition> {
  if (!states) {
    return {}
  }

  return states.reduce((dictionary, state) => {
    return { ...dictionary, [state.name]: state }
  }, {} as Record<string, StateDefinition>)
}

/**
 * Selects a map of order statuses to their display state
 * @param states - Array of state definitions
 * @returns A record where keys are status names and values are display state objects
 */
export function selectDisplayStateByStatus(
  states: StateConfig['states'] | undefined,
): Record<string, DisplayState | null> {
  if (!states) {
    return {}
  }

  return states.reduce((acc, state) => {
    acc[state.name] = state.displayState
    return acc
  }, {} as Record<string, DisplayState | null>)
}

// .filter() returns a new array on every call, but TQ v5 applies replaceEqualDeep() to select
// results — if the output is structurally identical to the previous result the old array reference
// is returned. orderPanels is therefore reference-stable between polls when panel config is unchanged.
export function selectSidebarOrderPanels(stateConfig: StateConfig): OrderPanel[] {
  return (stateConfig.orderPanels ?? []).filter(panel => (panel.states?.length ?? 0) > 0)
}

/**
 * Returns a list of human-readable diagnostic strings for any structural or
 * referential issues found in a loaded stateConfig. An empty array means the
 * config looks healthy. Intended for Sentry reporting only — not user-facing.
 */
export function diagnoseStateConfig(config: StateConfig): string[] {
  const issues: string[] = []
  const stateNames = new Set((config.states ?? []).map(s => s.name))

  if (!config.states?.length) {
    issues.push('states: empty or missing — no order states are defined')
    // Cross-reference checks below require a populated stateNames set. Returning
    // early avoids flooding Sentry with spurious "unknown state" entries that all
    // stem from this single root cause.
    return issues
  }

  if (!config.features) {
    issues.push('features: missing — feature toggles are not configured')
  }

  if (!config.layout) {
    issues.push('layout: missing — dashboard cannot render without a layout')
  } else if (!config.layout.middle?.columns?.length) {
    issues.push('layout.middle.columns: empty or missing — no columns defined in layout')
  } else {
    if (typeof (config.layout.middle as any).columnOrder === 'string') {
      issues.push('layout.middle.columnOrder: legacy flat string value — Firestore data needs migration to { desktop?, mobile? } object shape')
    }

    config.layout.middle.columns.forEach((col, i) => {
      const label = `layout.middle.columns[${i}] (id="${col.id}")`
      if (!col.states?.length) {
        issues.push(`${label}: no states defined — column will always be empty`)
      } else {
        const unknown = col.states.filter(s => !stateNames.has(s))
        if (unknown.length) {
          issues.push(`${label}: references unknown state(s): ${unknown.join(', ')}`)
        }
      }
    })
  }

  if (config.orderPanels?.length) {
    config.orderPanels.forEach((panel, i) => {
      const label = `orderPanels[${i}] (id="${panel.id}")`
      const unknown = (panel.states ?? []).filter(s => !stateNames.has(s))
      if (unknown.length) {
        issues.push(`${label}: references unknown state(s): ${unknown.join(', ')}`)
      }
    })
  }

  return issues
}

/**
 * Derives status options from the state configuration
 * Filters states that can be manually transitioned to (canForceTransition: true)
 * and returns them as options sorted by progression level
 *
 * @param states - The state configuration
 * @returns An array of status options
 */
export function deriveStatusOptions(states: StateConfig['states'] | undefined): StatusOption[] {
  if (!states) {
    return []
  }

  return states
    .filter(state => state.canForceTransition === true)
    .map(state => ({
      value: state.name,
      label: state.displayState?.stateDisplayName || state.name,
      color: state.displayState?.color,
      progressionLevel: state.progressionLevel,
    }))
    // Sort by progression level
    .sort((a, b) => (a.progressionLevel || 0) - (b.progressionLevel || 0))
}
